import { useEffect, useState } from 'react';
import type { MembrePublic } from '@camellie/shared';
import { estAdulte } from '@camellie/shared';
import { api, ErreurApi } from '../api/client';
import { PaveNumerique } from '../composants/PaveNumerique';
import { Logo } from '../composants/Logo';

interface Props {
  onConnecte: (membre: MembrePublic, famille_id: string) => void;
}

const LONGUEUR_PIN = 6;

/**
 * Connexion en deux temps : on dit qui on est, puis on tape son PIN.
 *
 * Choisir son prenom avant de saisir le code coute une tape, mais evite deux
 * problemes : deux PIN identiques deviendraient ambigus, et la limitation
 * d'essais peut etre comptee par personne plutot que globalement — sinon l'un
 * de nous pourrait verrouiller l'autre en se trompant cinq fois.
 */
export function EcranConnexion({ onConnecte }: Props) {
  const [membres, setMembres] = useState<MembrePublic[] | null>(null);
  const [choisi, setChoisi] = useState<MembrePublic | null>(null);
  const [pin, setPin] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [secoue, setSecoue] = useState(false);

  useEffect(() => {
    api
      .membres()
      .then((r) => setMembres(r.membres.filter(estAdulte)))
      .catch((e: unknown) => {
        // Le detail est affiche tel quel : a deux utilisateurs, un message
        // precis vaut mieux qu'un message rassurant. « Serveur injoignable »
        // couvrait aussi bien une coupure reseau qu'un 500 ou une page de
        // defi intercalee, ce qui ne laissait rien a diagnostiquer.
        setErreur(e instanceof ErreurApi ? e.message : 'Erreur inattendue.');
      });
  }, []);

  /**
   * La soumission part du geste, pas d'un effet declenche par la longueur du
   * PIN. Un effet aurait eu besoin de `occupe` dans ses dependances, donc se
   * serait relance en changeant cet etat — et son nettoyage aurait annule sa
   * propre requete en vol. Le mode strict de React, qui joue chaque effet deux
   * fois, produit exactement le meme blocage. Ici, il n'y a rien a annuler.
   */
  async function soumettre(membre: MembrePublic, code: string) {
    setOccupe(true);
    setErreur(null);
    try {
      const r = await api.connexion(membre.id, code);
      onConnecte(r.membre, r.famille_id);
    } catch (e: unknown) {
      setPin('');
      setSecoue(true);
      window.setTimeout(() => setSecoue(false), 400);
      setErreur(e instanceof ErreurApi ? e.message : 'Connexion impossible.');
    } finally {
      setOccupe(false);
    }
  }

  function ajouterChiffre(membre: MembrePublic, c: string) {
    if (occupe || pin.length >= LONGUEUR_PIN) return;
    const suivant = pin + c;
    setPin(suivant);
    if (suivant.length === LONGUEUR_PIN) void soumettre(membre, suivant);
  }

  if (!membres) {
    return (
      <div className="connexion">
        <div className="marque">
          <Logo taille={84} />
          <p>{erreur ?? 'Chargement...'}</p>
        </div>
      </div>
    );
  }

  if (!choisi) {
    return (
      <div className="connexion">
        <div className="marque">
          <Logo taille={84} />
          <p>Qui es-tu ?</p>
        </div>
        <div className="choix-membres">
          {membres.map((m) => (
            <button
              key={m.id}
              type="button"
              className="bouton pleine-largeur"
              onClick={() => {
                setChoisi(m);
                setErreur(null);
              }}
            >
              <span className="point-membre" style={{ background: m.couleur }} />
              {m.prenom}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="connexion">
      <div className="marque">
        <h1>{choisi.prenom}</h1>
        <p>Ton code a {LONGUEUR_PIN} chiffres</p>
      </div>

      <div>
        <div className={'points-pin' + (secoue ? ' erreur' : '')} aria-hidden="true">
          {Array.from({ length: LONGUEUR_PIN }, (_, i) => (
            <span key={i} className={'point-pin' + (i < pin.length ? ' plein' : '')} />
          ))}
        </div>
        <p className="message-erreur" role="status">
          {erreur ?? ''}
        </p>
      </div>

      <PaveNumerique
        desactive={occupe}
        onChiffre={(c) => ajouterChiffre(choisi, c)}
        onEffacer={() => setPin((p) => p.slice(0, -1))}
        onRetour={() => {
          setChoisi(null);
          setPin('');
          setErreur(null);
        }}
      />
    </div>
  );
}
